import { ActionState, ActionStateEnum, EditModeEnum, IdleCameraState, IdleConstruction, IdleDecoration } from "..";
import { EntityNode } from "../../plan/EntityNodes/EntityNode";
import { EntityManager } from "../../plan/EntityManager";
import { Command } from "../../../../constants";
import { PlanManager } from "../../../../cocosPlan";
import { ArrangementGroup, arrangementManager, ArrangementObject, Commands, Comment, DoubleQuarterStaircase, Entity, Events, eventsManager, GeometryPrimitive, math, planManager, RenderCamera, roomManager, SavaneConstants, SceneConstants, Sphere, Staircase, TechnicalElement, Transform } from "@rhinov/savane-js";

declare var cc: any;


//polyfill ie11
if (typeof Math.sign === 'undefined') { Math.sign = function(x) { return x > 0 ? 1 : x < 0 ? -1 : x; } }
/**
 * Created by rhinov15 on 29/07/2016.
 */

export class EditEntityState extends ActionState {

    state: number = ActionStateEnum.EditEntity;
    entity: Entity;
    processed: boolean = false;
    allowTulip: boolean;
    startRotateMode: boolean;
    node: EntityNode | null;
    editMode: number;
    baseVectorNormalize: math.vec3 | null = null;
    baseAngle: math.vec3 | null = null;
    _clickOffset: math.vec3 = math.vec3.create();
    _lastCursorPosition: math.vec3 | null = null;
    _dragging: boolean = false;

    ctrlPressed: boolean = false;
    altPressed: boolean = false;
    cPressed: boolean = false;
    hPressed: boolean = false;
    pressDownTime = null;

    previousState: number = PlanManager.getInstance().getAction();
    widthEqualsModifiedWidth: boolean = false;

    constructor(entity: Entity, position: math.vec3, toRotate?: boolean, rotateMode?: number, toResize?: string, tulip?: boolean) {
        super();
        
        //Create SavaneJS entity
        this.entity = entity;
        PlanManager.getInstance().updateItemsSelected([this.entity], false, false);
        
        

        this.allowTulip = tulip === undefined ? true : tulip;

        this.node = EntityManager.getInstance().getNode(entity);
        if (this.node === null) {
            console.error('Entity' + entity.id + ' edit but node === null / Type: ' + entity.entityType);
        }
        this.entity.startTemporary();

        this.editMode = this.chooseStartingMode(this.entity, position, toRotate ?? false, rotateMode ?? 0, toResize ?? '');
        if (this.editMode === EditModeEnum.rotate || this.editMode === EditModeEnum.inclinaison) {
            this.startRotateMode = true;
        }
        else {
            this.startRotateMode = false;
        }

       
        math.vec3.set(this._clickOffset, position[0] - entity.position[0], position[1] - entity.position[1], this.entity.position[2]);

        

        if ((this.entity as DoubleQuarterStaircase).modifiedWidth !== undefined) {
            //update double staicase second leg width
            this.widthEqualsModifiedWidth = (this.entity as DoubleQuarterStaircase).width === (this.entity as DoubleQuarterStaircase).modifiedWidth;
        }
    }

    getAction(): number {
        return this.state;
    }

    getEntityType(): number | null {
        return this.entity.entityType;
    }

    keyPressed(keyCode: number, ctrl?: boolean, alt?: boolean): ActionState | null {
        if (keyCode == cc.KEY.escape) {
            this.mouseClickCancelled(this._lastCursorPosition ?? math.vec3.create());
            return null;
        }
        if (keyCode === cc.KEY.ctrl || ctrl) {
            this.ctrlPressed = true;
        }
        if (keyCode === cc.KEY.alt || alt) {
            this.altPressed = true;
        }
        if (keyCode === cc.KEY.s) {
            PlanManager.getInstance().toggleSnap();
        }
        if (keyCode === cc.KEY.c) {
            this.cPressed = true;
        }
        if (keyCode === cc.KEY.h) {
            this.hPressed = true;
        }
        if (keyCode === cc.KEY.e) {
            this.editMode = EditModeEnum.rotate;
        }
        if (keyCode === cc.KEY.i) {
            this.editMode = EditModeEnum.inclinaison;
        }
        return null;
    }

    createCommand(entity: Entity): Command {
        switch(entity.entityType) {
            case SceneConstants.EntityType.Comment:
                return(new Commands.EditCommentCommand(entity as Comment));

            case SceneConstants.EntityType.GeometryPrimitive:
                return(new Commands.EditGeometryPrimitiveCommand(entity as GeometryPrimitive));

            case SceneConstants.EntityType.TechnicalElement:
                return(new Commands.EditTechnicalElementCommand(entity as TechnicalElement));

            case SceneConstants.EntityType.Staircase:
                return(new Commands.EditStaircaseCommand(entity as Staircase));

            case SceneConstants.EntityType.RenderCamera:
                return(new Commands.EditRenderCameraCommand(entity as RenderCamera, false as any));

            default:
                return(new Commands.EditEntityCommand(entity));
        }
    }

    keyUnPressed(keyCode: number, ctrl?: boolean, maj?: boolean): ActionState | null {
        if (keyCode === cc.KEY.e || keyCode === cc.KEY.i) {
            this.editMode = EditModeEnum.drag;

            if (this.startRotateMode) {
                if (this._dragging) {
                    if (this.entity.isValid(PlanManager.getInstance().world.currentScene!.currentFloor!)) {
                        PlanManager.getInstance().executeCommand(this.createCommand(this.entity));
                    }
                    else {
                        this.entity.endTemporary();
                    }
                }
                var idleState = this.createIdleState(this.entity);
                eventsManager.instance.dispatch(Events.CHANGE_STATE, idleState);
            }
        }
        if (keyCode === cc.KEY.c) {
            this.cPressed = false;
        }
        if (keyCode === cc.KEY.h) {
            this.hPressed = false;
        }
        if (keyCode == cc.KEY.ctrl) {
            this.ctrlPressed = false;
        }
        if (keyCode == cc.KEY.alt) {
            this.altPressed = false;
        }
        return null;
    }

    mouseClickDown(cursorPosition: math.vec3, doubleClick: boolean, hoverEntities: Entity[], mouseButton: number): ActionState | null {
        if (mouseButton === 2) {
            PlanManager.getInstance().mouseClickCancelled();
        }
        return null;
    }

    mouseClickUp(cursorPosition: math.vec3, elapsedTime: number, hoverEntities: Entity[], mouseButton: number): ActionState | null {
        // Only if the entity was moved (i.e. dragged)
        if (this._dragging) {
            var valid = this.entity.isValid(PlanManager.getInstance().world.currentScene!.currentFloor!);

            if (this.entity.isTechnicalElementEntity() && ((this.entity as TechnicalElement).objectId === SceneConstants.TechnicalElementType.pole) && (this.altPressed)) {
                valid = true;
            }

            if (valid) {
                PlanManager.getInstance().executeCommand(this.createCommand(this.entity));
            }
            else {
                this.entity.endTemporary();
            }
        }

        // Inform WebGL to update this object as it was moved
        eventsManager.instance.dispatch(Events.COMMAND_EXECUTED,
            {
                name: Commands.CommandEnum.EditEntityCommand,
                undo: false,
                datas: {
                    id: this.entity.id,
                    type: this.entity.entityType,
                    select: true
                }
            });

        PlanManager.getInstance().updateItemsSelected([this.entity], true, mouseButton === 2);

        var idleState = this.createIdleState(this.entity);
        eventsManager.instance.dispatch(Events.CHANGE_STATE, idleState);
        return null;
    }

    moveMouseCursor(cursorPosition: math.vec3, hoverEntities?: Entity[], planpos?: math.vec3): ActionState | null {
        super.moveMouseCursor(cursorPosition, hoverEntities, planpos);
        var entity = this.entity as ArrangementObject | TechnicalElement
        // Redraw Node
        var node = EntityManager.getInstance().getNode(this.entity);
        if (!node) {
            return null;
        }
        node.needRedraw = true;

        if (this.entity.locked === true) {
            return null;
        }

        if ((this._lastCursorPosition !== null) && (math.vec3.distance(this._lastCursorPosition, cursorPosition) > SavaneConstants.PositionTolerance)) {
            this._dragging = true;
        }

        //close tulip on first move
        if (!this.processed) {
            this.processed = true;
            eventsManager.instance.dispatch(Events.HIDE_TULIP);
        }
        if (this.editMode === EditModeEnum.drag) {
            var computePos = math.vec3.create();
            var savedPosition = this.entity.position;
            math.vec3.set(computePos, cursorPosition[0] - this._clickOffset[0], cursorPosition[1] - this._clickOffset[1], this.entity.position[2]);
            this.entity.position = computePos;
            switch (this.entity.entityType) {
                case SceneConstants.EntityType.ArrangementObject:
                case SceneConstants.EntityType.ArrangementGroup:
                    if (PlanManager.getInstance().snapEnabled) {
                        planManager.snapArrangement(this.entity as ArrangementGroup | ArrangementObject, PlanManager.getInstance().world.currentScene!.currentFloor!);
                        if (!this.entity.isValid(PlanManager.getInstance().world.currentScene!.currentFloor!)) {
                            this.entity.position = savedPosition;
                            if (this.entity.temporary !== null) {
                                this.entity.temporary.recomputeValidity = true;
                            }
                        }
                    }
                    else {
                        if (this.entity.temporary !== null) {
                            this.entity.temporary.recomputeValidity = true;
                        }
                    }
                    this.node!.setValidity(this.entity.isValid(PlanManager.getInstance().world.currentScene!.currentFloor!));
                    (this.entity as ArrangementObject).updateFloorHeightFromAnchor(roomManager.getRoomAtPosition(this.entity.position, PlanManager.getInstance().world.currentScene!.currentFloor!)!, PlanManager.getInstance().world.currentScene!.currentFloor!);
                    break;
                case SceneConstants.EntityType.Staircase:
                case SceneConstants.EntityType.RenderCamera:
                case SceneConstants.EntityType.TechnicalElement:
                case SceneConstants.EntityType.GeometryPrimitive:
                    if (PlanManager.getInstance().snapEnabled) {
                        planManager.snapArrangement(this.entity as Staircase | TechnicalElement | GeometryPrimitive, PlanManager.getInstance().world.currentScene!.currentFloor!);
                        if (!this.entity.isValid(PlanManager.getInstance().world.currentScene!.currentFloor!)) {
                            this.entity.position = savedPosition;
                            if (this.entity.temporary !== null) {
                                this.entity.temporary.recomputeValidity = true;
                            }
                        }
                    }
                    else {
                        if (this.entity.temporary !== null) {
                            this.entity.temporary.recomputeValidity = true;
                        }
                    }
                    if ((this.entity as TechnicalElement).objectId !== SceneConstants.TechnicalElementType.pole || !this.altPressed) {
                        this.node!.setValidity(this.entity.isValid(PlanManager.getInstance().world.currentScene!.currentFloor!));
                    }
                    break;
            }
            this.node!.update(undefined);
        } else if (this.editMode === EditModeEnum.rotate) {
            var rotationCenter = math.vec2.fromValues(this.entity.position[0], this.entity.position[1]);
            var angle = this.entity.angle;
            var currentRotationPoint = math.vec2.fromValues(cursorPosition[0], cursorPosition[1]);
            var currentVector = math.vec2.fromValues(currentRotationPoint[0] - rotationCenter[0], currentRotationPoint[1] - rotationCenter[1]);
            var currentVectorNormalize = math.vec2.create();

            math.vec2.normalize(currentVectorNormalize, currentVector);
            if (this.baseVectorNormalize === null) {
                var cos = Math.cos(Math.PI * 2 - angle);
                var sin = Math.sin(Math.PI * 2 - angle);
                this.baseVectorNormalize = math.vec2.create();
                math.vec2.set(this.baseVectorNormalize, currentVectorNormalize[0] * cos - currentVectorNormalize[1] * sin, currentVectorNormalize[0] * sin + currentVectorNormalize[1] * cos);
            }
            //Calculate cosinus of angle using dot product
            var dotProduct = math.vec2.dot(this.baseVectorNormalize, currentVectorNormalize);
            //Calculate angle
            angle = Math.acos(dotProduct);
            if (isNaN(angle)) {
                angle = 0;
            }
            //Calculate sign of angle
            var directionSign = this.baseVectorNormalize[0] * currentVectorNormalize[1] - this.baseVectorNormalize[1] * currentVectorNormalize[0];
            if (directionSign < 0)
                angle = -angle;

            //Apply rotation
            if (this.ctrlPressed && this.altPressed) {
                //clamp angle to PI/2
                this.entity.angle = Math.round(angle / (Math.PI / 2)) * (Math.PI / 2);
            } else if (this.ctrlPressed) {
                //clamp angle to PI/2
                this.entity.angle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
            } else {
                this.entity.angle = angle;
            }
        } else if (this.editMode === EditModeEnum.inclinaison && this._lastCursorPosition) {
            var cp = math.vec2.fromValues(cursorPosition[0], cursorPosition[1]);
            var oldRealHeight = entity.realHeight;

            var factor = 10;
            if (PlanManager.getInstance().magnetRotation) {
                factor = 80;
            }

            if (!(this.entity as any).$$dy) {
                (this.entity as any).$$dy = 0;
            }

            (this.entity as any).$$dy += (cp[1] - this._lastCursorPosition[1]) / factor;
            var value: number = entity.inclinaison + (this.entity as any).$$dy;

            if (PlanManager.getInstance().magnetRotation) {
                value = Math.round(value / 5) * 5;
            }

            if (Math.round(entity.inclinaison) !== value) {
                (this.entity as any).$$dy = 0;
                entity.inclinaison = value;
                var newRealHeight = entity.realHeight;
                var floorHeight = this.entity.floorHeight;
                var itemHeight = entity.height;

                var oldFloorHeight = floorHeight - ((oldRealHeight / 2) - (itemHeight / 2))
                var newFloorHeight = oldFloorHeight + ((newRealHeight / 2) - (itemHeight / 2));
                this.entity.floorHeight = newFloorHeight;
            }
        } else if (this.editMode === EditModeEnum.resizeH || this.editMode === EditModeEnum.resizeV) {
            var axis = this.editMode === EditModeEnum.resizeH ? 'length' : 'width';
            // Keep local scale
            var scale = math.vec3.clone(this.entity.transform.localScale);
            // Cancel potential scale
            var scale1 = math.vec3.create();
            math.vec3.set(scale1, 1, 1, 1);
            this.entity.transform.localScale = scale1;
            // Get inverted global matrix and global matrix
            var invGlobalmatrix = this.entity.transform.invertedGlobalMatrix;
            var globalMatrix = this.entity.transform.globalMatrix;
            // Re-apply scale to the object
            this.entity.transform.localScale = scale;

            var diameter = false;
            if ((axis === 'length') && (entity.length === undefined)) {
                diameter = true;
            }
            if ((axis === 'width') && (entity.width === undefined)) {
                diameter = true;
            }

            var mouseLocalPos = math.vec3.create();
            var decal = math.vec3.fromValues(0, 0, 0);
            if (diameter) {
                // And compute mouse pos in local repere
                math.vec3.subtract(mouseLocalPos, cursorPosition, this.entity.transform.globalPosition);
                var value = 0;
                if (axis === 'length') {
                    value = mouseLocalPos[0];
                } else {
                    value = mouseLocalPos[1];
                }
                var diff1 = ((this.entity as Sphere).diameter - Math.abs(value) * 2) / 2;
                (this.entity as Sphere).diameter -= diff1;

                math.vec3.transformMat4(decal, decal, globalMatrix);
                this.entity.transform.globalPosition = decal;
            } else {
                // And compute mouse pos in local repere
                math.vec3.transformMat4(mouseLocalPos, cursorPosition, invGlobalmatrix);

                var transform = new Transform(this.entity as any);
                transform.clone(this.entity.transform);
                transform.globalZRotation = 0;
                var toLocal = Transform.extractRotation(transform.invertedGlobalMatrix);

                var direction = math.vec3.fromValues(toLocal[4], toLocal[5], toLocal[6]);
                if (axis === 'length') {
                    direction = math.vec3.fromValues(toLocal[0], toLocal[1], toLocal[2]);
                }
                math.vec3.normalize(direction, direction);

                var diff: math.vec3 = math.vec3.fromValues(
                    Math.abs(direction[0]) * (entity.length - Math.abs(mouseLocalPos[0]) * 2) / 2,
                    Math.abs(direction[1]) * (entity.width - Math.abs(mouseLocalPos[1]) * 2) / 2,
                    Math.abs(direction[2]) * (entity.height - Math.abs(mouseLocalPos[2]) * 2) / 2,
                );

                entity.length -= diff[0];
                entity.width -= diff[1];
                entity.height -= diff[2];

                if ((this.entity as DoubleQuarterStaircase).modifiedWidth !== undefined && this.widthEqualsModifiedWidth) {
                    //update double staicase second leg width
                    (this.entity as DoubleQuarterStaircase).modifiedWidth -= diff[1];
                }

                decal[0] += Math.abs(direction[0]) * (-diff[0] / 2 * Math.sign(mouseLocalPos[0]));
                decal[1] += Math.abs(direction[1]) * (-diff[1] / 2 * Math.sign(mouseLocalPos[1]));

                math.vec3.transformMat4(decal, decal, globalMatrix);
                this.entity.transform.globalPosition = decal;
            }

            if (this.entity.temporary !== null) {
                this.entity.temporary.recomputeValidity = true;
            }

            this.node!.setValidity(this.entity.isValid(PlanManager.getInstance().world.currentScene!.currentFloor!));
        }

        if (this.cPressed && this.entity instanceof ArrangementObject) {
            var arrangements = arrangementManager.getArrangementObjectsAtPosition(this.entity.position, PlanManager.getInstance().world.currentScene!.currentFloor!);

            for (var i = 0; i < arrangements.length; i++) {
                if (Math.abs(arrangements[i].floorHeight + arrangements[i].height - this.entity.floorHeight) < SavaneConstants.PositionTolerance) {
                    var pos = math.vec3.create();
                    math.vec3.set(pos, arrangements[i].position[0], arrangements[i].position[1], this.entity.position[2]);

                    this.entity.angle = arrangements[i].angle;
                    this.entity.position = pos;
                }
            }
        }

        if (this.hPressed) {
            switch (this.entity.entityType) {
                case SceneConstants.EntityType.Staircase:
                    var room = roomManager.getRoomAtPosition(this.entity.position, PlanManager.getInstance().world.currentScene!.currentFloor!);

                    if (room !== null) {
                        (this.entity as Staircase).height = room.height; // Set height of the pole with the room pole
                    }
                    break;

                case SceneConstants.EntityType.TechnicalElement:
                    if ((this.entity as TechnicalElement).objectId === SceneConstants.TechnicalElementType.spotLight) {
                        var room = roomManager.getRoomAtPosition(this.entity.position, PlanManager.getInstance().world.currentScene!.currentFloor!);

                        if (room !== null) {
                            this.entity.floorHeight = room.height - 30; // Place the spotlight 1cm underneath the ceiling so it is a bit out of the ceiling
                        }
                    }
                    if ((this.entity as TechnicalElement).objectId === SceneConstants.TechnicalElementType.beam || (this.entity as TechnicalElement).objectId === SceneConstants.TechnicalElementType.rosette ||
                        (this.entity as TechnicalElement).objectId === SceneConstants.TechnicalElementType.frame) {
                        var room = roomManager.getRoomAtPosition(this.entity.position, PlanManager.getInstance().world.currentScene!.currentFloor!);

                        if (room !== null) {
                            this.entity.floorHeight = room.height - (this.entity as TechnicalElement).height; // Place the beam, rosette and frame to touch the ceiling
                        }
                    }
                    if ((this.entity as TechnicalElement).objectId === SceneConstants.TechnicalElementType.pole ||
                        (this.entity as TechnicalElement).objectId === SceneConstants.TechnicalElementType.stove ||
                        (this.entity as TechnicalElement).objectId === SceneConstants.TechnicalElementType.fireplace) {
                        var room = roomManager.getRoomAtPosition(this.entity.position, PlanManager.getInstance().world.currentScene!.currentFloor!);

                        if (room !== null) {
                            (this.entity as TechnicalElement).height = room.height; // Set height of the pole with the room pole
                        }
                    }
                    break;
            }
        }

        // Inform WebGL to update this object as it was moved
        eventsManager.instance.dispatch(Events.COMMAND_EXECUTED,
            {
                name: Commands.CommandEnum.EditEntityCommand,
                undo: false,
                datas: {
                    id: this.entity.id,
                    type: this.entity.entityType,
                    select: false
                }
            });

        this._lastCursorPosition = math.vec3.clone(cursorPosition);
        return null;
    }

    mouseClickCancelled(cursorPosition: math.vec3): ActionState | null {
        var idleState = this.createIdleState(this.entity);

        this.node!._isHovered = false;
        if (idleState) {
            eventsManager.instance.dispatch(Events.PLAN_STATE_CHANGED, idleState.getAction());
            eventsManager.instance.dispatch(Events.CHANGE_STATE, idleState);
        }

        this.entity.endTemporary();

        // Inform WebGL to update this object as it was moved
        eventsManager.instance.dispatch(Events.COMMAND_EXECUTED,
            {
                name: Commands.CommandEnum.EditEntityCommand,
                undo: false,
                datas: {
                    id: this.entity.id,
                    type: this.entity.entityType,
                    select: true
                }
            });

        return null;
    }

    isDecorationState(): boolean {
        switch (this.entity.entityType) {
            case SceneConstants.EntityType.UserPicture:
            case SceneConstants.EntityType.RenderCamera:
            case SceneConstants.EntityType.ArrangementObject:
            case SceneConstants.EntityType.ArrangementGroup:
                return (true);
                break;
        }
        return (false);
    }

    dispose() {
        this.entity.endTemporary();
        delete (this.entity as any).$$dy;
    }

    /**
     *
     * Create corresponding idle state for that type of entity
     *
     * @param entity
     * @returns {*}
     */
    createIdleState(entity: Entity) : ActionState | null {
        var state: ActionState | null = null;
        switch (entity.entityType) {
            case SceneConstants.EntityType.RenderCamera:
            case SceneConstants.EntityType.ArrangementObject:
            case SceneConstants.EntityType.ArrangementGroup:
            case SceneConstants.EntityType.GeometryGroup:
                state = new IdleDecoration();
                break;
            case SceneConstants.EntityType.Staircase:
            case SceneConstants.EntityType.TechnicalElement:
            case SceneConstants.EntityType.Comment:
                state = new IdleConstruction();
                break;

            case SceneConstants.EntityType.GeometryPrimitive:
                if (this.previousState === ActionStateEnum.IdleConstruction) {
                    state = new IdleConstruction();
                }
                else {
                    state = new IdleDecoration();
                }
                break;

            case SceneConstants.EntityType.UserPicture:
                state = new IdleCameraState();
                break;
        }
        return state;
    }

    chooseStartingMode(entity: Entity, position: math.vec3, toRotate: boolean, rotateMode: number, toResize: string): number {
        var mode = EditModeEnum.drag;
        switch (entity.entityType) {
            case SceneConstants.EntityType.UserPicture:
                if (position && this.node) {
                    var distance = Math.sqrt((position[0] - this.entity.position[0]) * (position[0] - this.entity.position[0]) + (position[1] - this.entity.position[1]) * (position[1] - this.entity.position[1])) / 10;
                    if (distance < this.node.getWidth() * 0.25) {
                        mode = EditModeEnum.drag;
                    } else {
                        mode = EditModeEnum.rotate;
                    }
                }
                break;
            default:
                if (toRotate) {
                    mode = rotateMode;
                } else if (toResize === 'width') {
                    mode = EditModeEnum.resizeV;
                } else if (toResize === 'length') {
                    mode = EditModeEnum.resizeH;
                } else {
                    mode = EditModeEnum.drag;
                }
                break;
        }
        return mode;
    }
}